import React from "react";
import { observer } from "mobx-react";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { AlertsRecipientResult } from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardDrilldown";
import {
  AlertsDashboardStore,
  AlertsDrawerView,
} from "../../../../stores/dashboards/AlertsDashboardStore";
import {
  getModernPageColors,
  modernDrawerBodySx,
  modernDrawerHeaderSx,
} from "../../../../../../styles/modernListingPageSx";
import RedemptionHint from "../redemption/RedemptionHint";
import AlertsListPanel from "./AlertsListPanel";
import AlertsAlertDetail from "./AlertsAlertDetail";
import AlertsEmailDetail from "./AlertsEmailDetail";

const DRAWER_Z_OFFSET = 2000;

const viewHeader = (
  view: AlertsDrawerView,
): { kicker: string; title: string; desc: string | null } => {
  if (view.type === "alert") {
    const alert = view.result?.alert;
    return {
      kicker: `Alert #${view.alertId}${alert ? ` · ${alert.type_code}` : ""}`,
      title: alert?.title || view.title,
      desc: null,
    };
  }
  if (view.type === "email") {
    const batch = view.result?.batch;
    return {
      kicker: "Client alert email",
      title: batch?.to_email ? `${view.title} · ${batch.to_email}` : view.title,
      desc: null,
    };
  }
  if (view.kind === "recipient") {
    const recipient = (view.result as AlertsRecipientResult | null)?.data
      .recipient;
    const parts = [
      recipient?.email,
      recipient?.customer_name
        ? `${recipient.customer_name} (#${recipient.customer_id})`
        : null,
    ].filter(Boolean);
    return {
      kicker:
        view.recipient?.kind === "ADMIN"
          ? "Admin recipient"
          : "Client user recipient",
      title: recipient?.name || view.title,
      desc:
        parts.length > 0
          ? parts.join(" · ")
          : "Everything this person received in the range.",
    };
  }
  const label =
    view.kind === "drilldown" && view.result && "label" in view.result.data
      ? view.result.data.label
      : view.title;
  return {
    kicker: view.kicker,
    title: label,
    desc:
      view.kind === "drilldown"
        ? "Alerts behind this metric for the applied filters. Click a row for its detail."
        : view.kind === "emails"
          ? "One row per email (batch). Click a row for its alerts and SendGrid events."
          : "One row per alert × admin. Click a row for the alert detail.",
  };
};

interface AlertsDrawerProps {
  store: AlertsDashboardStore;
}

const AlertsDrawer: React.FC<AlertsDrawerProps> = ({ store }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const customColors = theme.palette.customColors;
  const view = store.drawerTop;
  const header = view ? viewHeader(view) : null;
  const how =
    view?.type === "list" ? (view.result?.data.how ?? view.how) : null;

  const handleClose = () => {
    try {
      store.closeDrawer();
    } catch (error) {
      console.error(error);
    }
  };

  const handleBack = () => {
    try {
      void store.goBack();
    } catch (error) {
      console.error(error);
    }
  };

  const handleRefresh = () => {
    try {
      if (view) {
        store.track("drawer_refresh", {
          view: store.viewKind(view),
          title: view.title,
        });
        void store.fetchView(view.id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={store.drawerOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 880, md: 960 },
          maxWidth: "100vw",
          height: "100%",
          maxHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) =>
                muiTheme.zIndex.drawer + DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      {view && header ? (
        <>
          <Box sx={modernDrawerHeaderSx(theme)}>
            <Box display="flex" gap={1.5} alignItems="flex-start" minWidth={0}>
              {store.drawerViews.length > 1 ? (
                <IconButton
                  onClick={handleBack}
                  size="small"
                  aria-label="Back"
                  title="Back"
                >
                  <ArrowBackIcon />
                </IconButton>
              ) : null}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: customColors.lightBlue,
                  color: customColors.darkBlue,
                }}
              >
                <InsightsOutlinedIcon fontSize="small" />
              </Box>
              <Box minWidth={0}>
                <CustomTypography
                  variant="caption"
                  sx={{
                    color: colors.subtitle,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  {header.kicker}
                </CustomTypography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <CustomTypography
                    variant="heading5"
                    sx={{ color: colors.title, wordBreak: "break-word" }}
                  >
                    {header.title}
                  </CustomTypography>
                  <RedemptionHint how={how} />
                </Box>
                {header.desc ? (
                  <CustomTypography
                    variant="body4"
                    sx={{
                      mt: 0.5,
                      color: colors.subtitle,
                      fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    }}
                  >
                    {header.desc}
                  </CustomTypography>
                ) : null}
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
              <IconButton
                onClick={handleRefresh}
                size="small"
                aria-label="Refresh"
                title="Refresh with applied filters"
                disabled={view.loading}
              >
                {view.loading ? (
                  <CircularProgress size={18} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
              <IconButton onClick={handleClose} size="small" aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              ...modernDrawerBodySx(),
              flex: 1,
              minHeight: 0,
              overflow: "auto",
            }}
          >
            {view.type === "list" ? (
              <AlertsListPanel
                key={view.id}
                store={store}
                view={view}
                maxHeight={window.innerHeight - 350}
              />
            ) : view.type === "alert" ? (
              <AlertsAlertDetail key={view.id} store={store} view={view} />
            ) : (
              <AlertsEmailDetail key={view.id} store={store} view={view} />
            )}
          </Box>
        </>
      ) : null}
    </Drawer>
  );
};

export default observer(AlertsDrawer);
